import React from 'react'
import Icon from 'components/elements/Icon'
import { useRouter } from 'next/router'

import { useAppSelector } from 'redux/hooks'

import { COND_ID_COND_NAME, LANG_ID_TO_NAME } from 'types/collection'
import { HUMAN_READABLE_FORMAT } from 'types/deck'

import LabelDisplay from 'components/collectionV2/misc/LabelDisplay'

import { setCollectionQueryParams } from 'utils/queryParams/collection.queryParams'

import styles from './activeFilters.module.scss'
import { COLLECTION_GAME_TYPE } from 'redux/collectionV2/types'

type Props = {}

const IGNORED_PARAMS = [
  'username',
  'page',
  'pageSize',
  'collectionOrderBy',
  'orderDirection',
  'cardSearch',
  'quantityLogic',
  'priceSource',
]

const IGNORED_VALUES = ['undefined', 'null', 'true', '']
const GENERIC_MULTIPLE_LABELS = ['types', 'subTypes', 'superTypes', 'colors', 'rarity']

const CARD_SEARCH_PARAMS = [
  'name',
  'editions',
  'types',
  'subTypes',
  'superTypes',
  'formatLegality',
  'colorIdentity',
  'color',
  'rarity',
]

const KEY_RELABELS: Record<string, string> = {
  syntaxQuery: 'Syntax',
  treatment: '', // I feel like this one speaks for itself without a label
  editions: 'Set code',
  types: 'Type',
  subTypes: 'Subtypes',
  superTypes: 'Supertypes',
  formatLegality: 'Format Legality',
  colorIdentity: 'Filtering by color identity',
  cardName: 'Card name:',
  name: 'Card name:',
}

const QUANTITY_LOGIC_RELABELS: Record<string, string> = {
  'eq': '=',
  'gt': '>',
  'lt': '<',
  'gte': '>=',
  'lte': '<=',
}

const ActiveFilters = ({}: Props) => {
  const router = useRouter()

  const handleRemoveQueryParam = (param: string | string[]) => {
    const query = { ...router.query }

    if (!Array.isArray(param)) delete query[param]
    else param.forEach(p => delete query[p])

    const attemptingToFilterByCards = Object.keys(query).some(key => CARD_SEARCH_PARAMS.includes(key))

    if (!attemptingToFilterByCards) delete query.cardSearch

    router.push({ query })
  }

  const viewableFilters = Object.keys(router.query).reduce((acc, key) => {
    // if it's an empty string or undefined, don't show it
    if (router.query[key] === 'undefined' || router.query[key] === '') return acc
    if (!IGNORED_PARAMS.includes(key)) acc.push(key)

    return acc
  }, new Array<string>())

  if (viewableFilters.length === 0) return null

  return (
    <div className={styles.content}>
      <span className={styles.label}>Active filters </span>

      {viewableFilters.map(key => {
        const value = `${router.query[key]}`

        if (key === 'andTags' && router.query.andTags)
          return <TagsLabel type="andTags" ids={router.query.andTags} queryParameter="andTags" />

        if (key === 'orTags' && router.query.orTags)
          return <TagsLabel type="orTags" ids={router.query.orTags} queryParameter="orTags" />

        if (key === 'notTags' && router.query.notTags)
          return <TagsLabel type="notTags" ids={router.query.notTags} queryParameter="notTags" />

        if (key === 'condition' && router.query.condition)
          return (
            <GenericArrayLabel ids={router.query.condition} queryParameter="condition" displayMap={COND_ID_COND_NAME} />
          )

        if (key === 'collectionLanguage' && router.query.collectionLanguage)
          return (
            <GenericArrayLabel
              ids={router.query.collectionLanguage}
              queryParameter="collectionLanguage"
              displayMap={LANG_ID_TO_NAME}
            />
          )

        if (key === 'quantity' && router.query.quantity && router.query.quantityLogic)
          return (
            <button key={key} onClick={() => handleRemoveQueryParam(key)} className={styles.button}>
              <Icon name="close" />
              <span>Quantity</span>
              <span>{QUANTITY_LOGIC_RELABELS[`${router.query.quantityLogic}`]}</span>
              {value}
            </button>
          )

        if (key === 'formatLegality' && router.query.formatLegality)
          return (
            <GenericArrayLabel
              ids={router.query.formatLegality}
              queryParameter="formatLegality"
              displayMap={HUMAN_READABLE_FORMAT}
            />
          )

        if (key === 'game' && router.query.game)
          return (
            <button key={key} onClick={() => handleRemoveQueryParam(key)} className={styles.button}>
              <Icon name="close" />
              <span>Collection type</span>
              {/* @ts-ignore */}
              <span>{COLLECTION_GAME_TYPE[router.query.game] || N / A}</span>
            </button>
          )

        if (GENERIC_MULTIPLE_LABELS.includes(key))
          return <GenericArrayLabel key={key} title={key} ids={value} queryParameter={key} />

        return (
          <button key={key} onClick={() => handleRemoveQueryParam(key)} className={styles.button}>
            <Icon name="close" /> {KEY_RELABELS[key] ?? key} {IGNORED_VALUES.includes(value) ? '' : value}
          </button>
        )
      })}

      <button className={styles.clearAll} onClick={() => setCollectionQueryParams({}, false)}>
        <Icon name="trash alternate outline" /> Clear all filters
      </button>
    </div>
  )
}

export default ActiveFilters

type MultiLabelProps = {
  ids: string | string[]
  queryParameter: string
  title?: string
  displayMap?: Record<string, React.ReactNode>
}

const GenericArrayLabel = ({ ids, title, queryParameter, displayMap }: MultiLabelProps) => {
  const router = useRouter()

  const arrayOfIds = Array.isArray(ids) ? ids : ids.split(',')

  const handleRemoveQueryParam = (idToRemove: string) =>
    router.push({ [queryParameter]: arrayOfIds.filter(internalDragId => idToRemove !== internalDragId).join(',') })

  return (
    <>
      {arrayOfIds.map(id => (
        <button key={id} onClick={() => handleRemoveQueryParam(id)} className={styles.button}>
          <Icon name="close" />
          {title && <span>{KEY_RELABELS[title] ?? title}</span>}
          {!!displayMap && displayMap[id]}
          {!displayMap && id}
        </button>
      ))}
    </>
  )
}

const TagsLabel = ({ ids, type }: MultiLabelProps & { type: 'andTags' | 'orTags' | 'notTags' }) => {
  const tags = useAppSelector(state => state.collectionV2.tags)

  const tagIdsArray = Array.isArray(ids) ? ids : ids.split(',')

  return (
    <>
      {tagIdsArray.map(tagId => (
        <button
          key={tagId}
          onClick={() => {
            let updatedTags: string | undefined = tagIdsArray
              .filter(internalDragId => tagId !== internalDragId)
              .join(',')

            if (updatedTags.length === 0) updatedTags = undefined

            setCollectionQueryParams({ [type]: updatedTags })
          }}
          className={styles.button}>
          <Icon name="close" />
          <LabelDisplay className={styles.smallerGap} name={tags[tagId]?.name} color={tags[tagId]?.color} />
          <span>({type === 'andTags' ? 'AND' : type === 'orTags' ? 'OR' : 'NOT'})</span>
        </button>
      ))}
    </>
  )
}
